import React from 'react';
import { Search, Filter, ArrowUpDown, X, SlidersHorizontal } from 'lucide-react';
import { AlertFilters, AlertSeverity } from '../../types/alert';
import { US_LOCATIONS } from '../../services/locationData';

interface AlertFilterBarProps {
  filters: AlertFilters;
  onChange: (updated: AlertFilters) => void;
  totalResults: number;
}

export const AlertFilterBar: React.FC<AlertFilterBarProps> = ({
  filters,
  onChange,
  totalResults,
}) => {
  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filters, searchQuery: e.target.value });
  };

  const handleSeverityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filters, severity: e.target.value as AlertSeverity | 'ALL' });
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filters, state: e.target.value });
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filters, sortBy: e.target.value as any });
  };

  const handleReset = () => {
    onChange({
      searchQuery: '',
      severity: 'ALL',
      category: 'ALL',
      state: 'ALL',
      sortBy: 'severity',
    });
  };

  const hasActiveFilters =
    filters.searchQuery !== '' ||
    filters.severity !== 'ALL' ||
    filters.state !== 'ALL' ||
    filters.sortBy !== 'severity';

  return (
    <div className="bg-white border border-slate-200/90 rounded-2xl p-4 sm:p-5 shadow-xs space-y-3.5 mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Keyword Search */}
        <div className="relative">
          <label htmlFor="search-input" className="sr-only">Search alerts by keyword, county, or event</label>
          <input
            id="search-input"
            type="text"
            placeholder="Search storm, county, flood..."
            value={filters.searchQuery}
            onChange={handleQueryChange}
            className="w-full pl-9 pr-7 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white text-navy-950 placeholder:text-slate-400 font-sans transition-all"
          />
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3 pointer-events-none" />
          {filters.searchQuery && (
            <button
              onClick={() => onChange({ ...filters, searchQuery: '' })}
              className="absolute right-2.5 top-3 text-slate-400 hover:text-slate-600"
              aria-label="Clear search input"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* State Filter */}
        <div>
          <label htmlFor="state-filter" className="sr-only">Filter by US State</label>
          <select
            id="state-filter"
            value={filters.state}
            onChange={handleStateChange}
            className="w-full px-3 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 font-medium"
          >
            <option value="ALL">All US States & Territories</option>
            {US_LOCATIONS.map((loc) => (
              <option key={loc.code} value={loc.code}>
                {loc.name} ({loc.code})
              </option>
            ))}
          </select>
        </div>

        {/* Severity Filter */}
        <div>
          <label htmlFor="severity-filter" className="sr-only">Filter by Severity Level</label>
          <select
            id="severity-filter"
            value={filters.severity}
            onChange={handleSeverityChange}
            className="w-full px-3 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 font-medium"
          >
            <option value="ALL">All Severity Levels</option>
            <option value="Extreme">Extreme Severity Only</option>
            <option value="Severe">Severe & Higher</option>
            <option value="Moderate">Moderate Advisory</option>
            <option value="Minor">Minor Alert</option>
          </select>
        </div>

        {/* Sort Dropdown */}
        <div>
          <label htmlFor="sort-filter" className="sr-only">Sort alerts</label>
          <select
            id="sort-filter"
            value={filters.sortBy}
            onChange={handleSortChange}
            className="w-full px-3 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 font-medium"
          >
            <option value="severity">Sort: Highest Severity First</option>
            <option value="newest">Sort: Most Recently Issued</option>
            <option value="expiring">Sort: Expiring Soonest</option>
          </select>
        </div>
      </div>

      {/* Sub-bar with results count & reset */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-100 text-xs text-slate-600 font-mono">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-cyan-500" />
          <span className="font-bold text-navy-900">
            {totalResults} {totalResults === 1 ? 'BULLETIN' : 'BULLETINS'} MATCHING CRITERIA
          </span>
        </div>

        {hasActiveFilters && (
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold"
          >
            <X className="w-3.5 h-3.5" />
            <span>Reset Filters</span>
          </button>
        )}
      </div>
    </div>
  );
};
